/**
 * @fileoverview FAQ dataset management and validation utilities.
 * Provides functions for Apollo cache updates, dataset preparation,
 * confidence scoring, and feedback collection configuration.
 * @module helperMethods/faq/util
 */

import { graph } from "@/store/api";
import _ from "lodash";
import { gql } from "@apollo/client/core";
import { IExample, IFaq } from "../faqType";

/**
 * Updates the Apollo cache with new FAQ training dataset.
 *
 * @param {IFaq[]} newTrainingDataset - Array of FAQ intents to update in cache
 * @returns {boolean} Always returns true after successful update
 *
 * @example
 * updateApolloDataset(modifiedDataset);
 * // Apollo cache now contains updated dataset
 */
export function updateApolloDataset(newTrainingDataset: IFaq[]): boolean {
  graph.writeQuery({
    query: gql`
      query {
        faqAPI {
          dataset: getDataset
        }
      }
    `,
    data: {
      faqAPI: {
        dataset: newTrainingDataset,
      },
    },
  });

  return true;
}

/**
 * Prepares FAQ dataset for saving by removing duplicate variations.
 * Deduplicates variations within each intent based on text content (case-insensitive).
 *
 * @param {IFaq[]} dataset - Array of FAQ intents to prepare
 * @returns {IFaq[]} Cloned dataset with deduplicated variations
 *
 * @example
 * const cleanDataset = prepareDatasetToSave(rawDataset);
 * // Each intent now has unique variations only
 */
export function prepareDatasetToSave(dataset: IFaq[]): IFaq[] {
  const datasetToSave: IFaq[] = _.cloneDeep(dataset);
  datasetToSave.forEach((row) => {
    const distinctList: string[] = [];
    row.text = "";

    // Reduce in reverse direction to keep the latest version.
    row.variations = row.variations
      .reduceRight<IExample[]>((memo, variation) => {
        if (variation?.text) {
          const exist = distinctList.includes(variation.text.toLowerCase());
          if (!exist) {
            distinctList.push(variation.text.toLowerCase());
            memo.push(variation);
          }
        }
        return memo;
      }, [])
      .reverse();
  });

  return datasetToSave;
}

/**
 * Checks if a model has not been evaluated yet.
 *
 * @param {any} row - Row object containing model evaluation data
 * @returns {boolean} True if the model has no confidence score or batch test timestamp
 *
 * @example
 * if (modelUnevaluated(row)) {
 *   // Show "Not yet evaluated" indicator
 * }
 */
export function modelUnevaluated(row: any) {
  const invalidConfidence = !row.confidence || row.confidence === 0;
  const noBatchTestTimeStamp = !row.batchTestTimestamp;
  return invalidConfidence && noBatchTestTimeStamp;
}

/**
 * Checks if model confidence is below the configured threshold.
 *
 * @param {any} row - Row object containing model confidence score
 * @param {number} classifierThreshold - Minimum acceptable confidence threshold
 * @returns {boolean} True if confidence is below the threshold
 *
 * @example
 * if (modelConfidenceBelowThreshold(row, 0.75)) {
 *   // Show low confidence warning
 * }
 */
export function modelConfidenceBelowThreshold(row: any, classifierThreshold: number) {
  const modelConfidence = row.confidence || 0;
  return classifierThreshold > modelConfidence;
}

/**
 * Checks if current model confidence is below the published model's confidence.
 * Used to determine if a new model performs worse than the currently published one.
 *
 * @param {any} row - Row object containing model confidence score
 * @param {Array<Object>} models - Array of model objects with modelId, confidence, and timestamp
 * @param {string} publishedModelId - ID of the currently published model
 * @returns {boolean} True if current confidence is below published model's confidence
 *
 * @example
 * if (modelConfidenceBelowPublished(row, allModels, 'model-v2')) {
 *   // New model performs worse than published version
 * }
 */
export function modelConfidenceBelowPublished(
  row: any,
  models: {
    modelId: string;
    confidence: number;
    timestamp: string;
  }[],
  publishedModelId: string
) {
  const modelConfidence = row.confidence || 0;
  const publishedModelIndex = _.findIndex(models, (model) => model.modelId === publishedModelId);

  if (publishedModelIndex !== -1) {
    const publishedModel = models[publishedModelIndex];
    const publishedConfidence = publishedModel.confidence;
    return publishedConfidence > modelConfidence;
  } else {
    return false;
  }
}

/**
 * Formats confidence score as a percentage string.
 *
 * @param {string | number} confidence - Confidence score as number (0-1) or string
 * @returns {string} Formatted confidence with percentage symbol
 *
 * @example
 * formatConfidence(0.856);
 * // Returns: "85.6%"
 *
 * @example
 * formatConfidence("0.75");
 * // Returns: "0.75%"
 */
export function formatConfidence(confidence: string | number) {
  let formattedConfidence = confidence;
  if (typeof confidence === "string") {
    formattedConfidence = parseInt(confidence).toFixed(2);
  } else if (typeof confidence === "number") {
    // Make it a percentage with one decimal
    formattedConfidence = (confidence * 100).toFixed(1);
  }

  return formattedConfidence + "%";
}

/**
 * Determines if feedback collection is allowed for a content node.
 * Checks both global feedback settings and node-specific override settings.
 *
 * @param {any} node - Content node object with options.feedbackCollectionMode
 * @param {any} feedbackCollectionOptions - Global feedback collection configuration
 * @returns {boolean} True if feedback should be collected for this node
 *
 * @example
 * if (allowFeedbackOnContentNode(node, settings)) {
 *   // Show feedback buttons
 * }
 */
export const allowFeedbackOnContentNode = (node, feedbackCollectionOptions): boolean => {
  const mode = _.get(node, "options.feedbackCollectionMode", "default");
  const enabledForContentNodeOptions = _.get(
    feedbackCollectionOptions,
    "enabledForContentNode",
    false
  );
  const defaultContentNodeFeedback = _.get(
    feedbackCollectionOptions,
    "defaultContentNodeFeedback",
    false
  );
  return (
    enabledForContentNodeOptions &&
    ((mode === "default" && defaultContentNodeFeedback) || mode === "allow")
  );
};
